import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui';
import type {
  AccountingInvoicePostingMode,
  AccountingPaymentSyncMode,
} from '../../../../../services/accountingIntegration';
import {
  INVOICE_POSTING_MODE_OPTIONS,
  PAYMENT_SYNC_MODE_OPTIONS,
  selectClassName,
} from './accountingIntegrationUi';

interface Props {
  invoicePostingMode: AccountingInvoicePostingMode;
  paymentSyncMode: AccountingPaymentSyncMode;
  disabled: boolean;
  onInvoicePostingModeChange: (mode: AccountingInvoicePostingMode) => void;
  onPaymentSyncModeChange: (mode: AccountingPaymentSyncMode) => void;
}

export function AccountingWorkflowSettings({
  invoicePostingMode,
  paymentSyncMode,
  disabled,
  onInvoicePostingModeChange,
  onPaymentSyncModeChange,
}: Props) {
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Invoice workflow</h4>
      <FieldDescription className="mb-3">
        Choose whether CourtSync posts authorised invoices automatically or creates invoices for
        accountant review. Payments are only synced automatically in authorised/immediate mode.
      </FieldDescription>
      <FieldGroup className="gap-3">
        <Field>
          <FieldLabel htmlFor="invoice-posting-mode">Invoice posting mode</FieldLabel>
          <select
            id="invoice-posting-mode"
            className={selectClassName}
            value={invoicePostingMode}
            onChange={(event) =>
              onInvoicePostingModeChange(event.target.value as AccountingInvoicePostingMode)
            }
            disabled={disabled}
          >
            {INVOICE_POSTING_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          <FieldLabel htmlFor="payment-sync-mode">Payment sync mode</FieldLabel>
          <select
            id="payment-sync-mode"
            className={selectClassName}
            value={paymentSyncMode}
            onChange={(event) =>
              onPaymentSyncModeChange(event.target.value as AccountingPaymentSyncMode)
            }
            disabled={disabled || invoicePostingMode !== 'authorised'}
          >
            {PAYMENT_SYNC_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        {invoicePostingMode !== 'authorised' ? (
          <p className="text-xs text-amber-800 dark:text-amber-200/90">
            Payments cannot be created against draft or submitted invoices, so CourtSync will create
            the invoice only and skip payment sync for those transactions. Record the payment in your
            accounting software after review.
          </p>
        ) : null}
      </FieldGroup>
    </div>
  );
}
